import { Box, Button, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import dayjs from 'dayjs';
import React from 'react';
import { formatNumber } from '../util/NumberUtil';
import { humanize, plural } from '../util/StringUtil';
import { Transaction } from '../types/DashboardType';
import AppConfig from '../AppConfig';

export enum TransactionType {
  SPEND = 'Spend',
  SPEND_OVERPAYMENT = 'Spend',
  SPEND_PREPAYMENT = 'Spend',
  RECEIVE = 'Receive',
  RECEIVE_OVERPAYMENT = 'Receive',
  RECEIVE_PREPAYMENT = 'Receive',
  ACCREC = 'Receivable',
  ACCPAY = 'Payable',
  RECEIVE_TRANSFER = 'Transfer',
  SPEND_TRANSFER = 'Transfer',
}

export type TransactionsTableProps = {
  transactions: Transaction[];
  total: number;
  shortCode: string;
};

export function transactionTypeLabel(type: string) {
  return TransactionType[(type || '').replace('-', '_') as keyof typeof TransactionType] || humanize(type || '');
}

export function summariseTransactions(transactions: Transaction[]) {
  const amounts = transactions.map(t => Number(t.amount) || 0);
  const moneyIn = amounts.filter(a => a > 0).reduce((sum, a) => sum + a, 0);
  const moneyOut = amounts.filter(a => a < 0).reduce((sum, a) => sum + a, 0);
  return { moneyIn, moneyOut, net: moneyIn + moneyOut };
}

/** Oldest first: the oldest unreconciled items are usually the ones to chase. */
export function sortTransactions(transactions: Transaction[]) {
  return [...transactions].sort((a, b) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf());
}

export type AccountGroup = { account: string; transactions: Transaction[]; total: number };

/**
 * Group by bank account (accounts in alphabetical order, transactions without an account last),
 * with each account's transactions oldest first, so reconciling one account at a time is easy.
 */
export function groupTransactionsByAccount(transactions: Transaction[]): AccountGroup[] {
  const groups = new Map<string, Transaction[]>();
  transactions.forEach(t => {
    const key = t.accountName || '';
    groups.set(key, [...(groups.get(key) || []), t]);
  });
  return [...groups.entries()]
    .sort(([a], [b]) => (a === '' ? 1 : b === '' ? -1 : a.localeCompare(b)))
    .map(([account, items]) => ({
      account: account || 'No account',
      transactions: sortTransactions(items),
      total: items.reduce((sum, t) => sum + (Number(t.amount) || 0), 0),
    }));
}

function XeroButton(props: { shortCode: string; variant?: 'contained' | 'outlined' }) {
  return (
    <Button
      size="small"
      variant={props.variant || 'outlined'}
      endIcon={<OpenInNewIcon />}
      href={AppConfig.xeroOrganisationPath + props.shortCode}
      target="_blank"
      rel="noopener noreferrer"
    >
      Reconcile in Xero
    </Button>
  );
}

function SummaryItem(props: { label: string; value: number; color?: string }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {props.label}
      </Typography>
      <Typography sx={{ fontWeight: 600, color: props.color, fontVariantNumeric: 'tabular-nums' }}>{formatNumber(props.value)}</Typography>
    </Box>
  );
}

const headSx = { backgroundColor: '#f5f5f5', fontWeight: 600, py: 1, whiteSpace: 'nowrap' };

export default function TransactionsTable(props: TransactionsTableProps) {
  const { transactions, total, shortCode } = props;

  if (total > 0 && (!transactions || transactions.length === 0)) {
    return (
      <Stack alignItems="center" gap={1.5} sx={{ py: 2, textAlign: 'center' }}>
        <Typography>
          There are {total} transactions, which is too many to list here.
          <br />
          Review and reconcile them directly in Xero.
        </Typography>
        <XeroButton shortCode={shortCode} variant="contained" />
      </Stack>
    );
  }

  if (!transactions || transactions.length == 0) {
    return <Typography sx={{ m: 1 }}>Nothing left to reconcile.</Typography>;
  }

  const groups = groupTransactionsByAccount(transactions);
  const summary = summariseTransactions(transactions);
  // Hide columns Xero left empty for every row (often Name) rather than showing a blank column.
  const showName = transactions.some(t => t.name);
  const columnCount = showName ? 4 : 3;

  return (
    <Box>
      <Stack direction="row" alignItems="flex-end" flexWrap="wrap" gap={3} sx={{ mb: 2 }}>
        <SummaryItem label="Money in" value={summary.moneyIn} color="#1e6b3a" />
        <SummaryItem label="Money out" value={summary.moneyOut} />
        <SummaryItem label="Net" value={summary.net} />
        <Box sx={{ ml: 'auto' }}>
          <XeroButton shortCode={shortCode} />
        </Box>
      </Stack>
      {summary.net === 0 && transactions.length > 1 && (
        <Typography variant="body2" sx={{ mb: 1.5, color: '#8a4b00' }}>
          These transactions net to $0.00. They may be matching pairs that can be reconciled against each other.
        </Typography>
      )}
      <TableContainer sx={{ border: '1px solid #eee', borderRadius: 1, maxHeight: '60vh', '& td, & th': { px: 2, py: 0.75 } }}>
        <Table size="small" stickyHeader aria-label="Unreconciled transactions">
          <TableHead>
            <TableRow>
              <TableCell sx={headSx}>Date</TableCell>
              <TableCell sx={headSx}>Details</TableCell>
              {showName && <TableCell sx={headSx}>Name</TableCell>}
              <TableCell sx={headSx} align="right">
                Amount
              </TableCell>
            </TableRow>
          </TableHead>
          {groups.map(group => (
            <TableBody key={group.account}>
              <TableRow>
                <TableCell colSpan={columnCount - 1} sx={{ backgroundColor: '#f0f6f9', fontWeight: 600, py: 0.75 }}>
                  {group.account}
                  <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                    {plural(group.transactions.length, 'transaction')}
                  </Typography>
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ backgroundColor: '#f0f6f9', fontWeight: 600, py: 0.75, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}
                >
                  {formatNumber(group.total)}
                </TableCell>
              </TableRow>
              {group.transactions.map((transaction, index) => {
                const amount = Number(transaction.amount) || 0;
                return (
                  <TableRow key={`${group.account}-${index}`} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap', '&&': { pl: 4 } }}>{dayjs(transaction.date).format('DD/MM/YYYY')}</TableCell>
                    <TableCell>{transactionTypeLabel(transaction.type)}</TableCell>
                    {showName && <TableCell>{transaction.name || ''}</TableCell>}
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', color: amount > 0 ? '#1e6b3a' : undefined }}>
                      {formatNumber(amount)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          ))}
        </Table>
      </TableContainer>
    </Box>
  );
}
